import type { Article } from '@/hooks/useRecentArticles';
import type { LinkPreview } from '@/hooks/useLinkPreview';

const DB_NAME = 'ArticleCache';
const DB_VERSION = 1;
const STORE_NAME = 'articles';

interface CachedArticle extends Article {
  cachedAt: number;
}

/**
 * Initialize IndexedDB for article caching
 */
function initDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create object store with URL as key
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'url' });
        store.createIndex('cachedAt', 'cachedAt', { unique: false });
      }
    };
  });
}

/**
 * Cache an article in IndexedDB
 */
export async function cacheArticle(article: Article): Promise<void> {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const cachedArticle: CachedArticle = {
      ...article,
      cachedAt: Date.now(),
    };

    await new Promise<void>((resolve, reject) => {
      const request = store.put(cachedArticle);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();
  } catch (error) {
    console.warn('Failed to cache article:', error);
  }
}

/**
 * Cache multiple articles in IndexedDB
 */
export async function cacheArticles(articles: Article[]): Promise<void> {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    const cachedAt = Date.now();

    // Cache all articles in a single transaction
    const promises = articles.map(article => {
      const cachedArticle: CachedArticle = {
        ...article,
        cachedAt,
      };

      return new Promise<void>((resolve, reject) => {
        const request = store.put(cachedArticle);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    });

    await Promise.all(promises);
    db.close();
  } catch (error) {
    console.warn('Failed to cache articles:', error);
  }
}

/**
 * Get cached article from IndexedDB
 */
export async function getCachedArticle(url: string): Promise<Article | null> {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readonly');
    const store = transaction.objectStore(STORE_NAME);

    const cachedArticle = await new Promise<CachedArticle | null>((resolve, reject) => {
      const request = store.get(url);
      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });

    db.close();

    if (!cachedArticle) {
      return null;
    }

    // Check if cache is still valid (24 hours)
    const cacheAge = Date.now() - cachedArticle.cachedAt;
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours

    if (cacheAge > maxAge) {
      // Cache is expired, remove it
      await removeCachedArticle(url);
      return null;
    }

    // Return article without the cachedAt timestamp
    const { cachedAt, ...article } = cachedArticle;
    return article;
  } catch (error) {
    console.warn('Failed to get cached article:', error);
    return null;
  }
}

/**
 * Remove cached article from IndexedDB
 */
export async function removeCachedArticle(url: string): Promise<void> {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);

    await new Promise<void>((resolve, reject) => {
      const request = store.delete(url);
      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });

    db.close();
  } catch (error) {
    console.warn('Failed to remove cached article:', error);
  }
}

/**
 * Convert cached article to LinkPreview format
 */
export function articleToLinkPreview(article: Article): LinkPreview {
  return {
    title: article.title,
    description: article.description,
    image: article.thumbnail,
    siteName: article.source,
    favicon: undefined, // Articles don't have favicon info
    url: article.url,
    embed: undefined, // Articles don't have embed info
  };
}

/**
 * Clear old cached articles (older than 7 days)
 */
export async function clearOldCache(): Promise<void> {
  try {
    const db = await initDB();
    const transaction = db.transaction([STORE_NAME], 'readwrite');
    const store = transaction.objectStore(STORE_NAME);
    const index = store.index('cachedAt');

    const maxAge = 7 * 24 * 60 * 60 * 1000; // 7 days
    const cutoffTime = Date.now() - maxAge;

    const range = IDBKeyRange.upperBound(cutoffTime);

    await new Promise<void>((resolve, reject) => {
      const request = index.openCursor(range);
      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        } else {
          resolve();
        }
      };
      request.onerror = () => reject(request.error);
    });

    db.close();
  } catch (error) {
    console.warn('Failed to clear old cache:', error);
  }
}